import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, /* CardContent */ } from '../components/ui/Card';
import googlePlayBadge from '../assets/google-play-badge.svg';

function Landing() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-primary-50">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-sm bg-white/80 border-b border-neutral-200/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-[env(safe-area-inset-top)]">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-neutral-900">FactreeTech</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost" size="md">Sign in</Button>
              </Link>
              <Link to="/register">
                <Button size="md">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-neutral-900">
                Factree
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-800">
                  Farm Automation
                </span>
                Platform
              </h1>
              <p className="mt-6 text-xl leading-8 text-neutral-600 max-w-3xl mx-auto">
                Built for farmers, orchardists, agronmists, and agricultural professionals, FactreeTech is a modern platform that simplifies farm management with powerful tools for automation, data analysis, and real-time monitoring.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="xl" className="min-w-[160px]">
                  Get started
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="xl" className="min-w-[160px]">
                  Sign in
                </Button>
              </Link>
            </div>
            
            <p className="text-sm text-neutral-500">
              Sign up for free
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <h2 className="text-4xl font-bold tracking-tight text-neutral-900">
              Everything you need
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              A complete farm management solution, irrigation control, spraying, and compliance tracking, all in one platform.
            </p>
          </div>

          <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <Card className="text-center hover:shadow-large transition-all duration-300">
              <CardHeader>
                <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-6 mx-auto">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <CardTitle>Secure</CardTitle>
                <CardDescription>
                  Your farm data is protected with industry-leading security practices, including encryption and secure access controls
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center hover:shadow-large transition-all duration-300">
              <CardHeader>
                <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-6 mx-auto">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                  </svg>
                </div>
                <CardTitle>Modern UI</CardTitle>
                <CardDescription>
                  Easy to use interface designed for farmers, with intuitive navigation and responsive design
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="text-center hover:shadow-large transition-all duration-300">
              <CardHeader>
                <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-6 mx-auto">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <CardTitle>Certification</CardTitle>
                <CardDescription>
                  Built to help meet the needs of modern farm certification standards and compliance requirements
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-primary-900/20"></div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h2 className="text-4xl font-bold tracking-tight text-white">
              Ready to get started?
            </h2>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto">
              Join now to start using FactreeTech for free. Experience the future of farm management with our powerful tools and features designed specifically for farmers and agricultural professionals.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button variant="secondary" size="xl" className="min-w-[180px]">
                  Create your account
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="xl" className="min-w-[180px] border-white text-white hover:bg-white hover:text-primary-600">
                  Sign in now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-400">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-xl font-bold text-white">FactreeTech</span>
            </div>
            <p className="text-sm">
              FactreeTech.
            </p>
            <p className="text-xs text-neutral-500">
              Secure • Modern • Professional
            </p>
            <div className="flex justify-center pt-2">
              <a
                href="https://play.google.com/store/apps/details?id=com.factree.hhapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get FactreeTech on Google Play"
              >
                <img
                  src={googlePlayBadge}
                  alt="Get it on Google Play"
                  className="h-[52px] w-auto"
                />
              </a>
            </div>
            <p className="text-xs">
              <Link to="/terms" className="hover:text-neutral-200">Terms and Conditions</Link>
              <span className="mx-2">&middot;</span>
              <Link to="/privacy" className="hover:text-neutral-200">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
